import type { LogEntry } from "../types";

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const isRealLogId = (value?: string | null): boolean => !!value && UUID_REGEX.test(value);

const logFingerprint = (log: LogEntry) =>
  [log.tc || "", log.text || "", log.createdAt ?? "", log.type || ""].join("\u0001");

/**
 * Collapses entries that ended up sharing an id. Two optimistic logs that hit
 * the same `uq_logs_dedup` slot (same story, start_at and text — e.g. the same
 * transcript line launched twice) get remapped by the server to the SAME real
 * UUID, so from that moment they are one row: rendering both duplicates the
 * React key and leaves the extra card unaddressable by id.
 */
export const dedupeLogsById = (logs: LogEntry[]): LogEntry[] => {
  const positionById = new Map<string, number>();
  const deduped: LogEntry[] = [];

  for (const log of logs) {
    if (!log?.id) {
      deduped.push(log);
      continue;
    }
    const position = positionById.get(log.id);
    if (position === undefined) {
      positionById.set(log.id, deduped.length);
      deduped.push(log);
      continue;
    }
    // Keep the first slot (manual order) and overlay only the fields the
    // duplicate actually carries, so a later copy never blanks metadata.
    const merged: LogEntry = { ...deduped[position] };
    for (const [key, value] of Object.entries(log)) {
      if (value !== undefined) (merged as Record<string, any>)[key] = value;
    }
    deduped[position] = merged;
  }

  return deduped;
};

/**
 * Reconciles a server snapshot with entries that have not finished syncing.
 * Deleted ids are tombstones: an older socket snapshot must never resurrect
 * them while the explicit server delete (or a temp-id remap) is in flight.
 */
export const mergeLogsPreservingPending = (
  localLogs: LogEntry[] | undefined,
  remoteLogs: LogEntry[] | undefined,
  shouldPreserveLocalLog: (log: LogEntry) => boolean = () => false,
  isDeletedLog: (logId: string) => boolean = () => false,
): LogEntry[] => {
  const local = dedupeLogsById(
    (Array.isArray(localLogs) ? localLogs : []).filter((log) => !log.id || !isDeletedLog(log.id)),
  );
  const remote = dedupeLogsById(
    (Array.isArray(remoteLogs) ? remoteLogs : []).filter((log) => !log.id || !isDeletedLog(log.id)),
  );
  if (remote.length === 0) return local;
  if (local.length === 0) return remote;

  const localById = new Map(local.filter((log) => log.id).map((log) => [log.id, log]));
  const merged = remote.map((log) => {
    const localLog = log.id ? localById.get(log.id) : undefined;
    if (localLog && typeof localLog.sortOrder === "number" && typeof log.sortOrder !== "number") {
      return { ...log, sortOrder: localLog.sortOrder };
    }
    return log;
  });
  const remoteIds = new Set(remote.map((log) => log.id).filter(Boolean));
  const remoteFingerprints = new Set(remote.map(logFingerprint));

  for (const log of local) {
    if (!log.id) continue;
    if (isRealLogId(log.id) && !shouldPreserveLocalLog(log)) continue;
    if (remoteIds.has(log.id)) continue;
    if (remoteFingerprints.has(logFingerprint(log))) continue;
    merged.push(log);
  }

  return merged;
};
